import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { UpcomingStreamResponse, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

export type DateRange = 'today' | 'tomorrow' | 'this_week' | 'next_week' | 'this_month' | 'next_month' | 'last_month';

interface UpcomingStreamsState {
  upcomingStreams: UpcomingStreamResponse[];
  dateRange: DateRange;
  cacheInfo: CacheInfo | null;
  loading: boolean;
  error: string | null;
  setUpcomingStreams: (upcomingStreams: UpcomingStreamResponse[]) => void;
  setDateRange: (dateRange: DateRange) => void;
  setCacheInfo: (cacheInfo: CacheInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchUpcomingStreams: (accessToken: string, logout: () => void, forceRefresh?: boolean, dateRange?: DateRange) => Promise<void>;
}

export const useUpcomingStreamsStore = create<UpcomingStreamsState>()(devtools((set, get) => ({
  upcomingStreams: [],
  dateRange: 'this_week',
  cacheInfo: null,
  loading: false,
  error: null,
  setUpcomingStreams: (upcomingStreams) => set({ upcomingStreams, error: null }),
  setDateRange: (dateRange) => set({ dateRange }),
  setCacheInfo: (cacheInfo) => set({ cacheInfo }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  fetchUpcomingStreams: async (accessToken: string, logout: () => void, forceRefresh = false, dateRange?: DateRange) => {
    const range = dateRange ?? get().dateRange;
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (forceRefresh) {
        params.set('forceRefresh', 'true');
      }
      params.set('dateRange', range);

      const response = await fetch(`${API_BASE_URL}/api/upcoming-streams?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch upcoming streams: ${response.statusText}`);
      }

      const data = await response.json();
      set({
        upcomingStreams: data.upcomingStreams,
        cacheInfo: data.cacheInfo ?? null,
        loading: false,
        dateRange: range
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'upcoming-streams-store' }));
